import { useState, useEffect, useLayoutEffect } from "react";
import { formatStamp } from "../lib/richText";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { getProject, updateProjectStatus, updateTaskStatus, addTaskToProject, updateProjectLifecycle, setRenewalDismissed, verifyProject, markProjectLive, getIntakeForms, shareIntakeForm, getPendingIntakeRequests, resendIntakeShare, downloadIntakeFormDocx, API_BASE } from "../lib/api";
import { getFormById } from "../lib/intakeForms";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Loader2,
  ArrowLeft,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Globe,
  Target,
  Sparkles,
  Search,
  User,
  Building,
  Calendar,
  Copy,
  Mail,
  Send,
  FileText,
  ExternalLink,
  Link2,
  FileSignature,
  Download,
  Printer,
  ScanEye,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import UnifiedClientConversation from "../components/UnifiedClientConversation";
import { TaskFreshnessPill } from "../components/TaskFreshnessPill";
import ClientFilesPanel from "../components/ClientFilesPanel";
import ClientCompetitorReports from "../components/ClientCompetitorReports";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const PRODUCT_LABELS = {
  marketing: "Marketing Products",
  web_design: "Web Design Products",
  seo_aeo: "SEO/AEO Products",
  prospect_tools: "ProspectID & ProspectMax",
  digital_marketing: "Digital Marketing",
  ai_tools: "AI Tools",
};

const PRODUCT_ICONS = {
  marketing: Target,
  web_design: Globe,
  seo_aeo: Search,
  prospect_tools: Sparkles,
  digital_marketing: Target,
  ai_tools: Sparkles,
};

const STATUS_OPTIONS = ["pending", "in_progress", "on_hold", "completed", "cancelled"];
const TASK_STATUS_OPTIONS = ["pending", "in_progress", "completed", "blocked"];

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [intakeForms, setIntakeForms] = useState([]);
  const [pendingIntakes, setPendingIntakes] = useState([]);
  const [viewingForm, setViewingForm] = useState(null);
  // Resolved filename map for every file_id referenced from the order
  // (signed contract + creative/other order attachments). Populated once per
  // load so the links at the bottom of the Order Summary can show the real
  // filename rather than "Download file".
  const [fileNames, setFileNames] = useState({});

  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    task_type: "external",
    due_date: "",
  });

  // Lifecycle prompt — appears when admin marks project completed
  const [lifecyclePromptOpen, setLifecyclePromptOpen] = useState(false);
  const isAdmin = ["super_admin", "manager"].includes(user?.role);

  const fetchProject = async () => {
    try {
      const { data } = await getProject(projectId);
      setProject(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch project:", error);
    } finally {
      setLoading(false);
    }
  };

  // Force scroll to top synchronously BEFORE paint, every time projectId changes.
  // useLayoutEffect runs before the browser paints, so admins never see the page mid-scroll.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [projectId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      await fetchProject();
      try {
        const [intakeRes, pendingRes] = await Promise.all([
          getIntakeForms({ project_id: projectId }),
          getPendingIntakeRequests({ project_id: projectId }),
        ]);
        setIntakeForms(intakeRes.data || []);
        setPendingIntakes(pendingRes.data || []);
      } catch (e) {
        // non-fatal
      }
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        document.documentElement.scrollTop = 0;
      });
    })();
  }, [projectId]);

  const refreshIntakeState = async () => {
    try {
      const [intakeRes, pendingRes] = await Promise.all([
        getIntakeForms({ project_id: projectId }),
        getPendingIntakeRequests({ project_id: projectId }),
      ]);
      setIntakeForms(intakeRes.data || []);
      setPendingIntakes(pendingRes.data || []);
    } catch (e) {}
  };

  // Resolve filenames for every order attachment (signed contract + file_ids).
  // Fetched once per project load so the Order Summary attachment links can
  // show the real filename rather than a generic "Download file".
  useEffect(() => {
    if (!project) return;
    const ids = [
      ...(project.signed_contract_file_id ? [project.signed_contract_file_id] : []),
      ...(project.file_ids || []),
    ].filter((id) => id && !fileNames[id]);
    if (ids.length === 0) return;
    (async () => {
      const next = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const { data } = await axios.get(`${API_BASE}/api/files/${id}/info`);
          if (data?.filename) next[id] = data.filename;
        } catch (e) { /* ignore — fall back to generic label */ }
      }));
      if (Object.keys(next).length > 0) setFileNames((prev) => ({ ...prev, ...next }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateProjectStatus(projectId, newStatus);
      await fetchProject();
      // After marking completed, ask admin where to move it next
      if (newStatus === "completed" && isAdmin) {
        setLifecyclePromptOpen(true);
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleVerify = async () => {
    if (!window.confirm("Verify this order? The Ops Coordinator will be notified to begin processing.")) return;
    try {
      await verifyProject(projectId);
      toast.success("Order verified — Ops Coordinator has been notified.");
      await fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify order");
    }
  };

  const handleMarkLive = async () => {
    if (!window.confirm("Mark this order LIVE? This confirms proofs are approved / order is running and silences start-date and 48h follow-up reminders.")) return;
    try {
      await markProjectLive(projectId);
      toast.success("Order marked live — reminders silenced.");
      await fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark order live");
    }
  };

  const handleLifecycleSelect = async (stage) => {
    try {
      await updateProjectLifecycle(projectId, stage);
      toast.success(stage === "monthly_active" ? "Moved to Monthly Active Clients" : "Moved to Archived Clients");
      setLifecyclePromptOpen(false);
      await fetchProject();
    } catch (e) {
      toast.error("Failed to move project");
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      const refreshed = await fetchProject();
      // Auto-trigger lifecycle prompt when ALL tasks done
      if (newStatus === "completed" && isAdmin && refreshed?.tasks?.length > 0) {
        const allDone = refreshed.tasks.every((t) => t.status === "completed");
        if (allDone && refreshed.status !== "completed") {
          await updateProjectStatus(projectId, "completed");
          await fetchProject();
          setLifecyclePromptOpen(true);
        }
      }
    } catch (error) {
      console.error("Failed to update task status:", error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await addTaskToProject(projectId, taskForm);
      setTaskForm({ title: "", description: "", task_type: "external", due_date: "" });
      setDialogOpen(false);
      fetchProject();
    } catch (error) {
      console.error("Failed to add task:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: "status-pending",
      in_progress: "status-in_progress",
      completed: "status-completed",
      on_hold: "status-on_hold",
      cancelled: "status-cancelled",
      blocked: "status-blocked",
    };
    return classes[status] || "status-pending";
  };

  const getTaskTypeBadgeClass = (type) => {
    return type === "internal" ? "task-internal" : "task-external";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <p className="text-muted-foreground">Project not found</p>
        <Link to="/orders/active">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
        </Link>
      </div>
    );
  }

  const ProductIcon = PRODUCT_ICONS[project.product_type] || Globe;
  const externalTasks = project.tasks?.filter((t) => t.task_type === "external") || [];
  const internalTasks = project.tasks?.filter((t) => t.task_type === "internal") || [];
  const completedTasks = project.tasks?.filter((t) => t.status === "completed").length || 0;
  const totalTasks = project.tasks?.length || 0;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Pending Verification banner — super admin only */}
      {project.verified === false && user?.role === "super_admin" && (
        <Card className="border-amber-300 bg-amber-50" data-testid="verify-order-banner">
          <CardContent className="py-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-amber-900">This order is awaiting your verification.</div>
              <p className="text-sm text-amber-800 mt-1">
                Review the details below. Once verified, the Ops Coordinator will be notified to begin processing
                {" (intake forms, brief prep, etc.)."}
              </p>
            </div>
            <Button
              onClick={handleVerify}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="verify-order-btn"
            >
              Verify Order
            </Button>
          </CardContent>
        </Card>
      )}
      {project.verified === false && user?.role !== "super_admin" && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="awaiting-verification-banner">
          <CardContent className="py-3">
            <div className="text-sm text-amber-900">
              <b>Awaiting super-admin verification.</b> Lee Ann or Daryl will review this order shortly. You'll be notified once it's released for processing.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mark Live banner — shown once the order is verified but not yet
          confirmed live. Both internal + rep can mark live (the rep when
          proofs are approved, the internal team when launch is confirmed
          on our side). Clicking silences the start-date + 48h nudges. */}
      {project.verified !== false && !project.is_live && !["completed", "archived", "cancelled"].includes((project.status || "").toLowerCase()) && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="mark-live-banner">
          <CardContent className="py-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-emerald-900">Order is not confirmed live yet.</div>
              <p className="text-sm text-emerald-800 mt-1">
                {user?.role === "rep"
                  ? "Once your client approves proofs and the order is running, mark it live so Ready Prospect stops chasing you for approval."
                  : "Once launch is confirmed on our side, mark it live so the rep and client know we're underway. Silences the start-date and 48h follow-up reminders."}
              </p>
            </div>
            <Button
              onClick={handleMarkLive}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mark-live-btn"
            >
              Mark Live
            </Button>
          </CardContent>
        </Card>
      )}
      {project.is_live && (
        <Card className="border-emerald-300 bg-emerald-50/40" data-testid="is-live-banner">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="text-sm text-emerald-900">
              <b>LIVE</b> — launch confirmed{project.live_marked_by_name ? ` by ${project.live_marked_by_name}` : ""}{project.live_marked_at ? ` on ${new Date(project.live_marked_at).toLocaleDateString()}` : ""}.
            </div>
            <span
              className="text-[10px] font-semibold text-emerald-900 bg-emerald-200 rounded-full px-2 py-0.5"
              data-testid="live-chip"
            >
              LIVE
            </span>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/orders/active"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Projects
          </Link>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">
            {project.client_name}
            {project.agency_name && <span className="text-xl font-normal text-muted-foreground ml-2">/ {project.agency_name}</span>}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ProductIcon className="h-4 w-4" />
              <span>{PRODUCT_LABELS[project.product_type]}</span>
            </div>
            <Badge className={getStatusBadgeClass(project.status)}>
              {project.status.replace("_", " ")}
            </Badge>
            {project.created_at && (
              <span className="text-sm text-muted-foreground" data-testid="order-submitted-stamp">
                Submitted {formatStamp(project.created_at)}
                {(project.created_by_name || project.rep_name) ? ` by ${project.created_by_name || project.rep_name}` : ""}
              </span>
            )}
            {project.verified && project.verified_at && (
              <Badge variant="outline" className="border-green-300 bg-green-50 text-green-800" title={`Verified${project.verified_by_name ? ` by ${project.verified_by_name}` : ""} on ${new Date(project.verified_at).toLocaleString()}`}>
                Verified
              </Badge>
            )}
          </div>
        </div>
        <Select value={project.status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[160px]" data-testid="project-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replace("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Project Info Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <User className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Client</p>
                <p className="font-medium">{project.client_name}</p>
                {project.client_company && (
                  <p className="text-xs text-muted-foreground">{project.client_company}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Building className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sales Rep</p>
                <p className="font-medium">{project.rep_name || "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Campaign Dates</p>
                <p className="font-medium">
                  {project.start_date || formatDate(project.created_at)}
                  {project.end_date ? ` — ${project.end_date}` : ""}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Top working area ──────────────────────────────────────────
          Two-column grid: condensed order summary + rep tasks on the left,
          conversation on the right. Keeps the context AND the back-and-forth
          side-by-side so onboarding chatter doesn't push the order details
          off-screen. */}
      <div className="grid gap-6 lg:grid-cols-2 items-start">

      {/* Order Summary — mirrors the rep's order form summary so admins see exactly what was ordered */}
      <div className="printable space-y-6">
        {/* Print-only header — only visible when printing/saving as PDF */}
        <div className="hidden print:block mb-4">
          <div className="flex items-end justify-between border-b border-gray-300 pb-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-black">Order Summary</h1>
              <p className="text-sm text-gray-700 mt-0.5">
                {project.client_company || project.client_name}
                {project.agency_name ? ` · ${project.agency_name}` : ""}
              </p>
            </div>
            <div className="text-right text-xs text-gray-600">
              <p>Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              <p>Order ID: {project.id}</p>
            </div>
          </div>
        </div>

        <Card className="border bg-secondary/30" data-testid="order-summary-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Order Summary</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="no-print"
              data-testid="print-order-summary-btn"
              title="Print or save this order summary as a PDF"
            >
              <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
            </Button>
          </CardHeader>
        <CardContent>
          {/* Order Notes — surfaced at the TOP of the summary so client-provided
              instructions never get buried. Styled high-contrast red/amber so
              it reads as "important, read first". */}
          {project.notes && (
            <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-3" data-testid="order-notes-highlight">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase text-red-700 tracking-wide">Order Notes</div>
                  <div className="text-sm text-red-900 whitespace-pre-wrap mt-1">{project.notes}</div>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-start gap-4">
              <span className="text-muted-foreground">Client</span>
              <div className="text-right">
                <p className="font-medium">{project.client_company || project.client_name}</p>
                {project.client_industry && <p className="text-xs text-muted-foreground">{project.client_industry}</p>}
                {project.client_website && (
                  <a href={project.client_website.startsWith("http") ? project.client_website : `https://${project.client_website}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline break-all">{project.client_website}</a>
                )}
                {project.client_email && <p className="text-xs text-muted-foreground">{project.client_email}</p>}
                {project.client_phone && <p className="text-xs text-muted-foreground">{project.client_phone}</p>}
              </div>
            </div>
            
            {(project.product_types?.length > 0 || project.product_type) && (
              <div className="flex justify-between items-start gap-4">
                <span className="text-muted-foreground">Products</span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {(project.product_types?.length ? project.product_types : [project.product_type]).map((t) => (
                    <Badge key={t} variant="outline" className="text-xs">{PRODUCT_LABELS[t] || t}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {(project.sub_product_names?.length > 0 || project.sub_products?.length > 0) && (
              <div className="flex justify-between items-start gap-4">
                <span className="text-muted-foreground">Items</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[70%]">
                  {(project.sub_product_names?.length ? project.sub_product_names : project.sub_products).map((sp, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {typeof sp === "string" && sp.includes(" ") ? sp : sp.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {project.channel_quantities && Object.keys(project.channel_quantities).length > 0 && (
              <div className="flex justify-between items-start gap-4">
                <span className="text-muted-foreground">Channel Goals</span>
                <div className="text-right space-y-1">
                  {Object.entries(project.channel_quantities).map(([channel, qty]) => {
                    if (!qty || (typeof qty === "object" && Object.values(qty).every((v) => !v))) return null;
                    const channelLabel = channel.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                    return (
                      <div key={channel} className="text-xs">
                        <span className="font-medium">{channelLabel}: </span>
                        {typeof qty === "object" ? (
                          Object.entries(qty).filter(([, v]) => v).map(([metric, v]) => (
                            <span key={metric} className="ml-1">{metric}: <span className="font-medium">{v}</span></span>
                          ))
                        ) : (
                          <span className="font-medium">{qty}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {project.geo_target && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Geo Target</span>
                <span className="font-medium text-right break-words">{project.geo_target}</span>
              </div>
            )}
            
            {project.demographics && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Demographics / Audience</span>
                <span className="font-medium text-right break-words max-w-[70%] whitespace-pre-wrap">{project.demographics}</span>
              </div>
            )}
            
            {project.ad_creative_source && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Ad Creative</span>
                <span className="font-medium">
                  {project.ad_creative_source === "attached" ? "Attached" : project.ad_creative_source === "to_come" ? "To Come" : project.ad_creative_source === "rp_to_build" ? "RP to Build" : project.ad_creative_source}
                </span>
              </div>
            )}
            
            {project.hosting_option && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Hosting</span>
                <span className="font-medium">
                  {project.hosting_option === "hosting_only" ? "Hosting Only" : project.hosting_option === "hosting_with_maintenance" ? "Hosting + Maintenance" : project.hosting_option}
                </span>
              </div>
            )}
            
            {project.page_count != null && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Page Count</span>
                <span className="font-medium">{project.page_count}</span>
              </div>
            )}
            
            {project.use_prospect_id && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">ProspectID</span>
                <div className="text-right">
                  <Badge className="bg-primary/10 text-primary border-0">Enabled</Badge>
                  {project.prospect_count && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {Number(project.prospect_count).toLocaleString()} prospects in list
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {(project.start_date || project.end_date || project.auto_renew) && (
              <div className="flex justify-between gap-4 items-start">
                <span className="text-muted-foreground">Term</span>
                <div className="text-right">
                  <span className="font-medium">
                    {project.start_date || "—"} → {project.auto_renew ? "Auto-Renew Monthly" : (project.end_date || "ongoing")}
                  </span>
                  {project.end_date && !project.auto_renew && (
                    <div className="mt-2">
                      {project.renewal_dismissed ? (
                        <Badge variant="outline" className="text-xs border-slate-300 bg-slate-50 text-slate-700">
                          One-time campaign — no renewal reminders
                        </Badge>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={async () => {
                            try {
                              await setRenewalDismissed(project.id, true);
                              toast.success("Renewal reminders turned off");
                              fetchProject();
                            } catch { toast.error("Failed to update"); }
                          }}
                          data-testid="dismiss-renewal-btn"
                          title="Stop renewal reminders for this project (e.g., one-time event campaigns)"
                        >
                          One-time campaign — turn off renewal reminders
                        </Button>
                      )}
                      {project.renewal_dismissed && isAdmin && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs ml-2"
                          onClick={async () => {
                            try {
                              await setRenewalDismissed(project.id, false);
                              toast.success("Renewal reminders re-enabled");
                              fetchProject();
                            } catch { toast.error("Failed to update"); }
                          }}
                          data-testid="re-enable-renewal-btn"
                        >
                          Re-enable
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(project.monthly_amount != null || project.contract_value != null) && (
              <div className="space-y-2">
                {project.monthly_amount != null && (
                  <div className="flex justify-between gap-4 items-start">
                    <span className="text-muted-foreground">Monthly Amount</span>
                    <div className="text-right">
                      <div className="font-medium">${Number(project.monthly_amount).toLocaleString()}/mo</div>
                    </div>
                  </div>
                )}
                {project.contract_value != null ? (
                  <div className="flex justify-between gap-4 items-start">
                    <span className="text-muted-foreground">Total Contract Value</span>
                    <div className="text-right">
                      <div className="font-medium">
                        ${Number(project.contract_value).toLocaleString()}
                        {project.contract_value_type && (
                          <span className={`ml-2 text-xs font-semibold ${project.contract_value_type === "retail" ? "text-amber-700" : "text-blue-700"}`}>
                            ({project.contract_value_type === "retail" ? "Retail" : "Wholesale"})
                          </span>
                        )}
                      </div>
                      {project.contract_value_type === "retail" && (
                        <p className="text-xs text-muted-foreground italic mt-1 max-w-[260px]">
                          Assumed normal 2× markup over wholesale unless notes indicate otherwise.
                        </p>
                      )}
                    </div>
                  </div>
                ) : project.auto_renew && project.monthly_amount != null ? (
                  <div className="flex justify-between gap-4 items-start">
                    <span className="text-muted-foreground">Total Contract Value</span>
                    <div className="text-right text-xs italic text-muted-foreground">Month-to-month (not totalled)</div>
                  </div>
                ) : null}
              </div>
            )}
            
            {/* Attachment quick-links — surfaced at the bottom of the Order
                Summary so admins/reps can jump straight to the contract and
                creatives without scrolling. Shows the real filename when we've
                resolved it; falls back to a generic label otherwise. */}
            {(project.signed_contract_file_id || (project.file_ids?.length || 0) > 0) && (
              <div className="pt-3 mt-3 border-t space-y-1.5" data-testid="order-attachment-links">
                {project.signed_contract_file_id && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <FileSignature className="h-4 w-4" /> Attached Contract
                    </span>
                    <a
                      href={`${API_BASE}/api/files/${project.signed_contract_file_id}/download`}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium truncate max-w-[260px]"
                      target="_blank" rel="noopener noreferrer"
                      data-testid="order-attached-contract-link"
                      title={fileNames[project.signed_contract_file_id] || "Download contract"}
                    >
                      {fileNames[project.signed_contract_file_id] || "Download contract"}
                    </a>
                  </div>
                )}
                {(project.file_ids || []).map((fid, i) => (
                  <div key={fid} className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Paperclip className="h-4 w-4" /> Attached Creative{(project.file_ids.length > 1) ? ` ${i + 1}` : ""}
                    </span>
                    <a
                      href={`${API_BASE}/api/files/${fid}/download`}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium truncate max-w-[260px]"
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`order-attached-creative-link-${fid}`}
                      title={fileNames[fid] || "Download attachment"}
                    >
                      {fileNames[fid] || "Download attachment"}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rep Tasks moved up into the top working area so the sales rep's
          onboarding to-dos sit alongside the order summary. Internal/team
          tasks remain further down. */}
      <Card className="border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Badge className="task-external">External</Badge>
            Rep Tasks ({externalTasks.length})
          </CardTitle>
          <CardDescription>Tasks for the sales rep to complete</CardDescription>
        </CardHeader>
        <CardContent>
          {externalTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No external tasks</p>
          ) : (
            <div className="space-y-3">
              {externalTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  projectId={projectId}
                  clientId={project.client_id}
                  pendingIntake={pendingIntakes.find((p) => p.form_type === task.linked_form)}
                  onStatusChange={handleTaskStatusChange}
                  onIntakeChanged={refreshIntakeState}
                  getStatusBadgeClass={getStatusBadgeClass}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Right column: Conversation + Files tabs */}
      {project?.id && project?.client_id && (
        <Tabs defaultValue="conversation" className="w-full lg:sticky lg:top-4" data-testid="conversation-files-tabs-top">
          <TabsList className="grid w-full grid-cols-2 max-w-xs">
            <TabsTrigger value="conversation" data-testid="tab-conversation-top">Conversation</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files-top">Files</TabsTrigger>
          </TabsList>
          <TabsContent value="conversation" className="mt-3">
            <UnifiedClientConversation
              clientId={project.client_id}
              currentOrderId={project.id}
              title="Client Conversation"
              heightClass="h-[720px]"
              hideViewOrderBtn={true}
              newestFirst={true}
              staleThresholdHours={24}
            />
          </TabsContent>
          <TabsContent value="files" className="mt-3">
            <ClientFilesPanel clientId={project.client_id} heightClass="h-[720px]" />
          </TabsContent>
        </Tabs>
      )}
      </div>
      {/* ── /Top working area ─────────────────────────────────────────── */}

      {/* Proposal & Contract */}
      {(project.proposal_id || project.signed_contract_file_id || project.proposal_changes_notes) && (
        <Card className="border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSignature className="h-5 w-5" /> Proposal & Contract
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {project.proposal_id && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-muted-foreground"><Link2 className="h-4 w-4" /> Linked Proposal</span>
                <Link to={`/proposals?open=${project.proposal_id}`} className="text-primary hover:underline font-medium" data-testid="view-linked-proposal">
                  View proposal →
                </Link>
              </div>
            )}
            {project.signed_contract_file_id && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-muted-foreground"><FileSignature className="h-4 w-4" /> Signed Contract</span>
                <a
                  href={`${API_BASE}/api/files/${project.signed_contract_file_id}/download`}
                  className="text-primary hover:underline font-medium flex items-center gap-1"
                  data-testid="download-signed-contract"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </div>
            )}
            {project.proposal_changes_notes && (
              <div>
                <p className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Changes from Original Proposal</p>
                <p className="whitespace-pre-wrap rounded bg-amber-50/50 border border-amber-200 p-3">{project.proposal_changes_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Competitor Reports attached to this client — auto-hides when empty */}
      {project?.client_id && (
        <ClientCompetitorReports clientId={project.client_id} clientName={project.client_business_name} />
      )}

      {/* Notes moved into the Order Summary header (Order Notes highlight) */}

      {/* Progress */}
      <Card className="border" id="intake-forms">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Intake Forms ({intakeForms.length})
          </CardTitle>
          <CardDescription>Completed intake submissions for this order</CardDescription>
        </CardHeader>
        <CardContent>
          {intakeForms.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No intake forms submitted yet. Reps can complete forms from the Rep Tasks list below; they'll attach here automatically and trigger an alert email.
            </p>
          ) : (
            <div className="space-y-2" data-testid="intake-forms-list">
              {intakeForms.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between rounded-md border bg-secondary/30 px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{f.form_name || f.form_type}</div>
                      <div className="text-xs text-muted-foreground">
                        Submitted by {f.submitted_by_name || "—"} · {new Date(f.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setViewingForm(f)}
                    data-testid={`view-intake-${f.id}`}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Progress */}
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg">Overall Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{completedTasks} of {totalTasks} tasks completed</span>
              <span className="font-medium">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Tasks Section */}
      <div className="flex items-center justify-between">
        <h2 className="font-outfit text-xl font-semibold">Tasks</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-task-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddTask} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Task Title</Label>
                <Input
                  id="title"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Enter task title"
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task description..."
                  rows={3}
                  data-testid="task-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task_type">Task Type</Label>
                <Select
                  value={taskForm.task_type}
                  onValueChange={(value) => setTaskForm({ ...taskForm, task_type: value })}
                >
                  <SelectTrigger data-testid="task-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">External (Rep)</SelectItem>
                    <SelectItem value="internal">Internal (Team)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-task-button">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Task"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Internal Tasks — rep's view hides this card entirely. Rep Tasks have
          moved up into the top working area next to the conversation. */}
      {user?.role !== "rep" && (
        <Card className="border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Badge className="task-internal">Internal</Badge>
              Team Tasks ({internalTasks.length})
            </CardTitle>
            <CardDescription>Tasks for the internal team to complete</CardDescription>
          </CardHeader>
          <CardContent>
            {internalTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No internal tasks</p>
            ) : (
              <div className="space-y-3">
                {internalTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    projectId={projectId}
                    clientId={project.client_id}
                    pendingIntake={pendingIntakes.find((p) => p.form_type === task.linked_form)}
                    onStatusChange={handleTaskStatusChange}
                    onIntakeChanged={refreshIntakeState}
                    getStatusBadgeClass={getStatusBadgeClass}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legacy bottom Conversation tabs removed — moved into the top working
          area so onboarding chatter sits beside the order details. */}

      {/* Lifecycle prompt: where does this completed order go? */}
      <Dialog open={lifecyclePromptOpen} onOpenChange={setLifecyclePromptOpen}>
        <DialogContent className="max-w-md" data-testid="lifecycle-prompt-dialog">
          <DialogHeader>
            <DialogTitle>Order Completed — Where does it go next?</DialogTitle>
            <DialogDescription>
              Pick the right bucket for {project?.client_company || project?.client_name || "this client"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              onClick={() => handleLifecycleSelect("monthly_active")}
              data-testid="move-to-monthly-active"
              className="text-left p-4 border-2 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1">
                <span className="h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center">↻</span>
                Monthly Active Client
              </div>
              <p className="text-sm text-muted-foreground ml-9">
                Recurring monthly work — SEO, content, marketing campaigns. They'll be able to submit change requests.
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleLifecycleSelect("archived")}
              data-testid="move-to-archived"
              className="text-left p-4 border-2 rounded-lg hover:border-zinc-400 hover:bg-zinc-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-zinc-800 mb-1">
                <span className="h-7 w-7 rounded-full bg-zinc-100 flex items-center justify-center">📦</span>
                Archived Client
              </div>
              <p className="text-sm text-muted-foreground ml-9">
                One-time project complete (e.g., website build). Reps can re-order a new campaign anytime.
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLifecyclePromptOpen(false)} data-testid="lifecycle-prompt-skip">Decide later</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View intake-form submission dialog */}
      <Dialog open={!!viewingForm} onOpenChange={(open) => !open && setViewingForm(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="view-intake-dialog">
          <DialogHeader>
            <DialogTitle>{viewingForm?.form_name || viewingForm?.form_type}</DialogTitle>
            <DialogDescription>
              Submitted by {viewingForm?.submitted_by_name || "—"} on {viewingForm && new Date(viewingForm.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pr-2">
            {viewingForm && Object.entries(viewingForm.data || {}).map(([key, value]) => {
              const label = key.replace(/_/g, " ");
              // Ranking table value
              if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && value[0] !== null) {
                const rows = value.filter((r) => Object.values(r).some((v) => v));
                if (rows.length === 0) return null;
                return (
                  <div key={key} className="py-2 border-b last:border-0">
                    <div className="text-sm text-muted-foreground capitalize mb-2">{label}</div>
                    <div className="overflow-x-auto rounded-md border">
                      <table className="w-full text-xs">
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} className="border-t first:border-t-0">
                              <td className="px-2 py-1.5 text-muted-foreground w-8 align-top">{i + 1}</td>
                              {Object.entries(row).map(([k, v]) => (
                                <td key={k} className="px-2 py-1.5 align-top">
                                  <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                                  <div className="text-sm">{v || "—"}</div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }
              // File attachment value
              if (value && typeof value === "object" && value.file_id) {
                return (
                  <div key={key} className="flex justify-between py-2 border-b last:border-0">
                    <span className="text-sm text-muted-foreground capitalize">{label}</span>
                    <a
                      href={`${API_BASE}/files/${value.file_id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {value.filename || "Attachment"}
                    </a>
                  </div>
                );
              }
              return (
                <div key={key} className="flex justify-between gap-3 py-2 border-b last:border-0">
                  <span className="text-sm text-muted-foreground capitalize shrink-0">{label}</span>
                  <span className="text-sm font-medium text-right max-w-[60%] whitespace-pre-wrap">
                    {Array.isArray(value) ? value.join(", ") : (value || "—")}
                  </span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FORM_NAMES = {
  meta_facebook: "Facebook / Meta Intake Form",
  seo_aeo: "SEO & AEO Intake Form",
  a2p_registration: "A2P Registration Form",
  web_design: "Website Questionnaire Form",
};

function TaskItem({ task, projectId, clientId, pendingIntake, onStatusChange, onIntakeChanged, getStatusBadgeClass }) {
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const isCompleted = task.status === "completed";
  
  const formLink = task.linked_form
    ? `${window.location.origin}/intake-forms?form=${task.linked_form}${projectId ? `&project=${projectId}` : ""}${clientId ? `&client=${clientId}` : ""}`
    : null;

  const handleCopyLink = async () => {
    if (!task.linked_form) return;
    try {
      const { data } = await shareIntakeForm({
        form_type: task.linked_form,
        form_name: FORM_NAMES[task.linked_form] || "Intake Form",
        client_id: clientId,
        project_id: projectId,
        // No recipient_email → no email is sent, but the server still mints a token
      });
      const link = data.public_url || formLink;
      navigator.clipboard.writeText(link);
      toast.success("Client-fillable link copied!");
    } catch (err) {
      // Last resort: copy the internal form link
      if (formLink) {
        navigator.clipboard.writeText(formLink);
        toast.success("Form link copied to clipboard!");
      }
    }
  };

  const handleDownloadDocx = async () => {
    if (!task.linked_form) return;
    const schema = getFormById(task.linked_form);
    if (!schema) {
      toast.error("This form can't be downloaded as Word");
      return;
    }
    setDownloadingDocx(true);
    try {
      await downloadIntakeFormDocx({ schema, formName: FORM_NAMES[task.linked_form] || schema.name });
      toast.success("Word doc downloaded — attach it to your client email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate the Word doc");
    } finally {
      setDownloadingDocx(false);
    }
  };

  const handleSendEmail = async () => {
    if (!clientEmail || !task.linked_form) return;
    setSending(true);
    try {
      await shareIntakeForm({
        form_type: task.linked_form,
        form_name: FORM_NAMES[task.linked_form] || "Intake Form",
        client_id: clientId,
        project_id: projectId,
        recipient_email: clientEmail,
        recipient_name: clientName || "",
      });
      toast.success(`Intake form sent to ${clientEmail}. They'll get a no-login link by email.`);
      setShowEmailInput(false);
      setClientEmail("");
      setClientName("");
      if (onIntakeChanged) onIntakeChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (!pendingIntake) return;
    setResending(true);
    try {
      await resendIntakeShare(pendingIntake.token);
      toast.success(`Reminder sent to ${pendingIntake.recipient_email}`);
      if (onIntakeChanged) onIntakeChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend reminder");
    } finally {
      setResending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "in_progress":
        return <AlertCircle className="h-4 w-4 text-blue-600" />;
      case "blocked":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-zinc-400" />;
    }
  };

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        isCompleted ? "bg-secondary/50" : "bg-card hover:bg-secondary/30"
      }`}
      data-testid={`task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isCompleted}
          onCheckedChange={(checked) => {
            onStatusChange(task.id, checked ? "completed" : "pending");
          }}
          data-testid={`task-checkbox-${task.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${isCompleted ? "line-through text-muted-foreground" : ""}`}>
              {task.title}
            </p>
            <TaskFreshnessPill status={task.status} dueDate={task.due_date} />
          </div>
          {task.description && (
            <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
          )}
          {task.assignee_name && (
            <p className="text-xs text-muted-foreground mt-1">Assigned to: {task.assignee_name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {getStatusIcon(task.status)}
          <Select
            value={task.status}
            onValueChange={(value) => onStatusChange(task.id, value)}
          >
            <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`task-status-${task.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status} className="text-xs">
                  {status.replace("_", " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending intake badge — shown when a share link was emailed but not yet submitted */}
      {pendingIntake && !isCompleted && (
        <div className="ml-8 mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`pending-intake-${task.id}`}>
          <div className="flex items-center gap-2 text-xs text-amber-900 min-w-0">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              Sent to <b>{pendingIntake.recipient_email}</b>
              {" · awaiting response since "}
              {new Date(pendingIntake.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {pendingIntake.resend_count > 0 && ` · resent ${pendingIntake.resend_count}×`}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={resending}
            className="h-7 text-xs"
            data-testid={`resend-intake-${task.id}`}
          >
            {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : (<><Send className="mr-1 h-3 w-3" /> Resend</>)}
          </Button>
        </div>
      )}

      {/* Linked Form Actions */}
      {formLink && !isCompleted && (
        <div className="ml-8 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">{FORM_NAMES[task.linked_form] || "Intake Form"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCopyLink} className="text-xs" data-testid={`copy-form-link-${task.id}`}>
              <Copy className="mr-1 h-3 w-3" /> Copy Link
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowEmailInput(!showEmailInput)} className="text-xs" data-testid={`email-form-${task.id}`}>
              <Mail className="mr-1 h-3 w-3" /> Email to Client
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadDocx}
              disabled={downloadingDocx}
              className="text-xs"
              data-testid={`download-form-docx-${task.id}`}
              title="Download as Word document — email it to the client from your own inbox (no portal link)"
            >
              {downloadingDocx
                ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                : <Download className="mr-1 h-3 w-3" />}
              Download Word
            </Button>
            <Link to={`/intake-forms?form=${task.linked_form}${projectId ? `&project=${projectId}` : ""}${clientId ? `&client=${clientId}` : ""}`}>
              <Button type="button" variant="outline" size="sm" className="text-xs" data-testid={`open-form-${task.id}`}>
                <ExternalLink className="mr-1 h-3 w-3" /> Fill Out Form
              </Button>
            </Link>
          </div>
          {showEmailInput && (
            <div className="space-y-2 mt-3 rounded-md border bg-blue-50/50 p-3" data-testid={`email-input-block-${task.id}`}>
              <p className="text-xs text-blue-900">
                Send the client a private, login-free link. They fill it out → it auto-attaches to this order.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  type="text"
                  placeholder="Client name (optional)"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="h-8 text-xs"
                  data-testid={`email-name-input-${task.id}`}
                />
                <Input
                  type="email"
                  placeholder="client@email.com"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="h-8 text-xs"
                  data-testid={`email-input-${task.id}`}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowEmailInput(false)} className="h-8 text-xs">
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSendEmail}
                  disabled={!clientEmail || sending}
                  className="h-8 text-xs"
                  data-testid={`send-form-email-${task.id}`}
                >
                  {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Send to Client"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
